import { API_BASE } from './apiUtils';
// authUtils.js - Authentication utility functions

/**
 * Single source of truth for reading role info out of userData (from localStorage or a
 * live /auth/user response). Roles are: USER, ADMIN, SUPER_ADMIN — anyone authenticated has
 * at least USER, which carries full day-to-day operational access (batches, gate passes,
 * invoices, purchases, etc); ADMIN adds people-management within their scope; SUPER_ADMIN is
 * everything, every organization.
 * @param {Object|string} userData - User data from localStorage, or a parsed user object
 * @param {string} role - Role name to check, case-insensitive
 * @returns {boolean}
 */
export const hasRole = (userData, role) => {
  try {
    if (!userData) return false;
    const parsedUser = typeof userData === 'string' ? JSON.parse(userData) : userData;
    const groupNames = Array.isArray(parsedUser?.groupNames)
      ? parsedUser.groupNames.map((g) => String(g).toUpperCase())
      : [];
    const groupsCsv = (parsedUser?.groups || '').toUpperCase();
    return groupNames.includes(role.toUpperCase()) || groupsCsv.includes(role.toUpperCase());
  } catch {
    return false;
  }
};

/**
 * Check if user has admin privileges. Kept as the stable public shape used across the app;
 * implemented on top of the single `hasRole` parser above so there's exactly one place that
 * reads groupNames/groups off userData.
 * @param {Object} userData - User data from localStorage
 * @returns {Object} - Object containing isAdmin, isSuperAdmin, and isAuthenticated flags
 */
export const checkAdminStatus = (userData) => {
  let isAdmin = false;
  let isSuperAdmin = false;

  try {
    if (userData) {
      const parsedUser = typeof userData === 'string' ? JSON.parse(userData) : userData;
      isSuperAdmin = hasRole(userData, 'SUPER_ADMIN') || !!parsedUser?.isSuperAdmin;
      isAdmin = isSuperAdmin || hasRole(userData, 'ADMIN') || !!parsedUser?.isAdmin;
    }
  } catch (error) {
    console.error('Error parsing user data:', error);
    isAdmin = false;
    isSuperAdmin = false;
  }

  return { isAdmin, isSuperAdmin, isAuthenticated: !!userData };
};

/**
 * Get user data from localStorage
 * @returns {Object|null} - Parsed user data or null
 */
export const getUserData = () => {
  try {
    const userData = localStorage.getItem('userData');
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error getting user data:', error);
    return null;
  }
};

/**
 * Set user data in localStorage
 * @param {Object} userData - User data to store
 */
export const setUserData = (userData) => {
  try {
    localStorage.setItem('userData', JSON.stringify(userData));
  } catch (error) {
    console.error('Error setting user data:', error);
  }
};

/**
 * Clear authentication data
 */
export const clearAuthData = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('userData');
  localStorage.removeItem('chatSessionId');
};

/**
 * Validate session with backend
 * @returns {Promise<Object>} - Validation result
 */
export const validateSession = async () => {
  const storedToken = localStorage.getItem('authToken');
  const storedUserData = localStorage.getItem('userData');

  // Always verify with the backend first — the session cookie may be valid even if localStorage was cleared
  try {
    const response = await fetch(`${API_BASE}/auth/user`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (response.ok) {
      const userResponse = await response.json();
      // Restore/refresh localStorage from the live backend response
      setUserData(userResponse);
      if (!storedToken) localStorage.setItem('authToken', 'session');
      return { isAuthenticated: true, userData: userResponse };
    }

    // Backend returned 401/403 — session is truly expired
    clearAuthData();
    return { isAuthenticated: false, error: 'Session expired' };
  } catch (networkErr) {
    // Cannot reach backend. Fall back to whatever is in localStorage.
    if (storedToken && storedUserData) {
      try {
        const parsed = JSON.parse(storedUserData);
        if (parsed?.username) {
          return { isAuthenticated: true, userData: parsed };
        }
      } catch (_) {}
    }
    clearAuthData();
    return { isAuthenticated: false, error: 'Network error' };
  }
};

/**
 * Login user
 * @param {Object} credentials - Username and password
 * @returns {Promise<Object>} - Login result
 */
export const loginUser = async (credentials) => {
  try {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        // Session cookie (set by the backend on this same response) is what actually
        // authenticates every subsequent request — nothing in the app reads a stored password
        // (httpBasic is disabled server-side; see SecurityConfig.java). Storing it here only
        // ever put the user's real password in plaintext in localStorage. Removed 2026-08-20.
        setUserData(data.user);
        const token = data.user.username + '-' + Date.now();
        localStorage.setItem('authToken', token);

        return { success: true, token, userData: data.user };
      } else {
        return { success: false, error: data.message || 'Authentication failed' };
      }
    } else {
      const errorData = await response.json();
      return { success: false, error: errorData.message || 'Authentication failed' };
    }
  } catch (err) {
    console.error('Login error:', err);
    return { success: false, error: 'Unable to connect to server. Please try again.' };
  }
};
